import { MarkerType, type Edge } from '@xyflow/react';

import { getPath, isPosition, EDGE_COLOR, type FlowNodeType } from './transform';
import { searchJmesPath } from '../../../utils';

type Obj = Record<string, unknown>;

/** Visual for a node role (color / icon / label). */
export interface IRoleVisual {
  label?: string;
  color?: string;
  icon?: string;
}

/**
 * A slot inside a resource section row. `isNode` slots become flowchart nodes;
 * non-node slots become **side operations** attached to the nearest ancestor node.
 * `sides` are leaf refs within the same row; `children` are nested arrays whose
 * rows are walked recursively (sub-nodes like as-int, or side arrays like
 * medicinal-product-intermediate).
 */
export interface IDeriveSlot {
  role: string;
  /** Path within the row to the ref — a single `{id}` or an array of `{id}`. */
  ref: string;
  isNode: boolean;
  sides?: IDeriveSlot[];
  children?: { arrayPath: string; slot: IDeriveSlot }[];
}

/** A top-level manufacturing section under a resource row. */
export interface IDeriveSection {
  /** Path from the resource row to the section array (e.g. `manufacturing.activeSubstance`). */
  path: string;
  /**
   * Scoping container for within-group edges: `row` = each section row is its own
   * lineage (active-substance); `resource` = the resource row is the container
   * (packaging: primary/secondary/batch share the package).
   */
  scope: 'row' | 'resource';
  /**
   * Intermediate array between the resource row and this section, walked one row
   * at a time (e.g. a package's `packaging` containers — box/blister). Nodes take
   * their **resource label** from the via row (the container), but stay scoped to
   * the outer resource (the package) for edge grouping, so containers in different
   * packages never chain together. `path` here is relative to the via row.
   */
  via?: { path: string; labelExpr?: string };
  slot: IDeriveSlot;
}

export interface IDeriveResource {
  /** Path to the resource array in `$mainForm` (e.g. `ingredients`). */
  root: string;
  /** JMESPath on a resource row → its display label (e.g. `join(' | ', [...])`). */
  labelExpr?: string;
  sections: IDeriveSection[];
}

export interface IDeriveConfig {
  /** Path to the flat MBO list in `$mainForm`. */
  operationsPath: string;
  /** Id field on MBO records + refs (default `id`). */
  idField?: string;
  /** MBO field holding main→main edges, and the target-id path within an entry. */
  relationField?: string;
  relationTargetPath?: string;
  /** Path on the MBO where a node's `{ x, y }` is persisted (default `position`). */
  positionField?: string;
  /** Ordered node roles for the flow chain. */
  chain: string[];
  /** Allowed adjacencies. */
  rules: Array<{ from: string; to: string }>;
  /** role → chain-group key (within-group edges are container-scoped; cross-group are all-to-all). */
  groups: Record<string, string>;
  /** role → visual. */
  visuals?: Record<string, IRoleVisual>;
  resources: IDeriveResource[];
}

export interface IDerivedSide {
  role: string;
  id: string;
  mbo?: Obj;
}
export interface IDerivedNode {
  /** MBO id. */
  id: string;
  role: string;
  /** Within-group scoping container (active-substance row / package). Internal use. */
  container?: string;
  /** Resource rows this node is linked from (many for multi-resource). */
  resources: Array<{ root: string; id: string; label?: string }>;
  mbo?: Obj;
  sides: IDerivedSide[];
}
export interface IDerivedEdge {
  source: string;
  target: string;
  /** `<from>-><to>` for auto edges, or `persisted`. */
  rule: string;
}
export interface IDerivedGraph {
  nodes: IDerivedNode[];
  edges: IDerivedEdge[];
  /** True when edges came from stored `relatedNode` overrides rather than auto-generation. */
  fromPersisted: boolean;
}

const refIds = (v: unknown): string[] => {
  if (v == null) return [];
  if (Array.isArray(v))
    return v
      .filter(x => !(x as Obj)?.toBeDeleted)
      .map(x => (x as Obj)?.id as string)
      .filter(Boolean);
  const id = (v as Obj)?.id as string;
  return id ? [id] : [];
};

/**
 * Derives the read-only flow-chart graph from the whole document (`$mainForm`):
 * main **nodes** (deduped by MBO id, multi-resource merged), their **side ops**,
 * and main→main **edges** — from stored `relatedNode` overrides if any exist,
 * else auto-generated (first present downstream role per the chain, container-
 * scoped within a group, gap-bridged only where a rule allows).
 */
export const deriveGraph = (
  mainForm: Obj,
  config: IDeriveConfig
): IDerivedGraph => {
  const idField = config.idField ?? 'id';
  const ops = (getPath(mainForm, config.operationsPath) as Obj[]) ?? [];
  const mboById = new Map<string, Obj>();
  for (const o of ops) {
    const id = getPath(o, idField) as string;
    if (id) mboById.set(id, o);
  }

  const nodeMap = new Map<string, IDerivedNode>();
  const upsertNode = (
    id: string,
    role: string,
    container: string,
    resource: { root: string; id: string; label?: string }
  ): IDerivedNode => {
    let n = nodeMap.get(id);
    if (!n) {
      n = { id, role, container, resources: [], mbo: mboById.get(id), sides: [] };
      nodeMap.set(id, n);
    }
    if (resource.id && !n.resources.some(r => r.root === resource.root && r.id === resource.id))
      n.resources.push(resource);
    return n;
  };

  const walkSlot = (
    row: Obj,
    slot: IDeriveSlot,
    container: string,
    resource: { root: string; id: string; label?: string },
    parent?: IDerivedNode
  ) => {
    if (slot.isNode) {
      for (const id of refIds(getPath(row, slot.ref))) {
        const node = upsertNode(id, slot.role, container, resource);
        for (const side of slot.sides ?? [])
          for (const sid of refIds(getPath(row, side.ref)))
            node.sides.push({ role: side.role, id: sid, mbo: mboById.get(sid) });
        for (const child of slot.children ?? []) {
          const childRows = (getPath(row, child.arrayPath) as Obj[]) ?? [];
          for (const cr of childRows) walkSlot(cr, child.slot, container, resource, node);
        }
      }
    } else {
      for (const id of refIds(getPath(row, slot.ref)))
        parent?.sides.push({ role: slot.role, id, mbo: mboById.get(id) });
    }
  };

  const computeLabel = (row: Obj, expr?: string): string | undefined => {
    if (!expr) return undefined;
    try {
      const v = searchJmesPath(row, expr);
      return v == null || v === '' ? undefined : String(v);
    } catch {
      return undefined; // join() over missing fields etc. — skip the label
    }
  };

  for (const res of config.resources) {
    const rows = (getPath(mainForm, res.root) as Obj[]) ?? [];
    for (const resRow of rows) {
      const resourceId = getPath(resRow, idField) as string;
      const resourceLabel = computeLabel(resRow, res.labelExpr);
      for (const sec of res.sections) {
        // A `via` section iterates an intermediate array (packaging containers):
        // the section path is relative to each via row, and nodes take their
        // resource/label from the via row while staying scoped to the package.
        const viaRows = sec.via
          ? ((getPath(resRow, sec.via.path) as Obj[]) ?? [])
          : [resRow];
        for (const viaRow of viaRows) {
          const resource = sec.via
            ? {
                root: `${res.root}.${sec.via.path}`,
                id: getPath(viaRow, idField) as string,
                label: computeLabel(viaRow, sec.via.labelExpr),
              }
            : { root: res.root, id: resourceId, label: resourceLabel };
          const secRows = (getPath(viaRow, sec.path) as Obj[]) ?? [];
          for (const secRow of secRows) {
            const container =
              sec.scope === 'row'
                ? (getPath(secRow, idField) as string)
                : resourceId;
            walkSlot(secRow, sec.slot, container, resource);
          }
        }
      }
    }
  }

  const nodes = [...nodeMap.values()];

  // Persisted overrides: relatedNode entries between two main nodes win over auto.
  const persisted = collectPersistedEdges(nodes, mboById, config);
  if (persisted) return { nodes, edges: persisted, fromPersisted: true };

  const ruleSet = new Set(config.rules.map(r => `${r.from}->${r.to}`));
  const idx = (role: string) => config.chain.indexOf(role);
  const edges: IDerivedEdge[] = [];
  for (const src of nodes) {
    const i = idx(src.role);
    if (i < 0) continue;
    for (let j = i + 1; j < config.chain.length; j++) {
      const P = config.chain[j];
      const sameGroup =
        config.groups[src.role] != null && config.groups[src.role] === config.groups[P];
      const targets = nodes.filter(
        n => n.role === P && (!sameGroup || n.container === src.container)
      );
      if (targets.length) {
        // First present downstream role — connect only if a rule allows it, else gap.
        if (ruleSet.has(`${src.role}->${P}`))
          for (const t of targets)
            edges.push({ source: src.id, target: t.id, rule: `${src.role}->${P}` });
        break;
      }
    }
  }
  return { nodes, edges, fromPersisted: false };
};

const ROW_GAP_Y = 190;
const NODE_GAP_X = 520;
const FALLBACK_VISUAL = { color: '#64748b', icon: '⚙️' };

/**
 * Maps a derived graph to React Flow nodes/edges for the read-only preview.
 * Node visuals come from the per-role `visuals` map; the MBO's saved
 * `positionField` wins when present. Otherwise a **role-based layered layout**:
 * each row is a role in the `chain` order (connection-rule order; only roles
 * with nodes get a row), so same-type operations share a horizontal line and
 * the vertical order follows the flow. Within a row, nodes are ordered by the
 * barycenter (mean x) of their connected neighbours — alternating down/up
 * sweeps — to keep edges short and reduce crossings.
 */
export const buildPreviewGraph = (
  derived: IDerivedGraph,
  opts: {
    visuals?: Record<string, IRoleVisual>;
    positionField?: string;
    /** Optional display sub-schema rendered inside each node ($node/$row scope). */
    cardSchema?: FlowNodeType['data']['cardSchema'];
    /** Role order (the flow chain) → drives vertical row placement. */
    chain?: string[];
  } = {}
): { nodes: FlowNodeType[]; edges: Edge[] } => {
  const posField = opts.positionField ?? 'position';
  const visuals = opts.visuals ?? {};
  const chain = opts.chain ?? [];

  const edges: Edge[] = derived.edges.map(e => ({
    id: `e-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    style: { strokeWidth: 2, stroke: EDGE_COLOR },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: EDGE_COLOR,
    },
    data: { rule: e.rule },
  }));

  // Rows = roles in chain order (compacted to roles that have nodes); any role
  // outside the chain (e.g. device-mfg) goes below in first-seen order.
  const roleOrder: string[] = [];
  for (const role of chain)
    if (!roleOrder.includes(role) && derived.nodes.some(n => n.role === role))
      roleOrder.push(role);
  for (const n of derived.nodes)
    if (!roleOrder.includes(n.role)) roleOrder.push(n.role);
  const rowOf = new Map(roleOrder.map((r, i) => [r, i]));

  const rows = new Map<number, IDerivedNode[]>();
  for (const n of derived.nodes) {
    const r = rowOf.get(n.role) ?? 0;
    (rows.get(r) ?? rows.set(r, []).get(r)!).push(n);
  }
  const rowIndices = [...rows.keys()].sort((a, b) => a - b);

  // Children adjacency (edges flow forward/down the chain).
  const children = new Map<string, string[]>();
  for (const e of derived.edges)
    (children.get(e.source) ?? children.set(e.source, []).get(e.source)!).push(e.target);

  const xById = new Map<string, number>();
  const centerRow = (list: IDerivedNode[]) => {
    const c = list.length;
    list.forEach((n, i) => xById.set(n.id, (i - (c - 1) / 2) * NODE_GAP_X));
  };
  for (const r of rowIndices) centerRow(rows.get(r)!);

  // Bottom-up placement: put each node above the barycenter (mean x) of its
  // children — which sit in lower, already-placed rows — so a node lines up
  // directly over what it feeds into. Nodes that would overlap are pushed apart
  // (min NODE_GAP_X) and the row re-centered on that barycenter, so the flow
  // funnels cleanly toward its sink.
  const placeRow = (list: IDerivedNode[]) => {
    const items = list.map(n => {
      const xs = (children.get(n.id) ?? [])
        .map(id => xById.get(id))
        .filter((v): v is number => v != null);
      const d = xs.length
        ? xs.reduce((a, b) => a + b, 0) / xs.length
        : (xById.get(n.id) ?? 0);
      return { n, d };
    });
    items.sort((a, b) => a.d - b.d);
    let prev = -Infinity;
    const placed = items.map(it => {
      const x = Math.max(it.d, prev + NODE_GAP_X);
      prev = x;
      return x;
    });
    const meanD = items.reduce((s, it) => s + it.d, 0) / items.length;
    const meanP = placed.reduce((s, x) => s + x, 0) / placed.length;
    const shift = meanD - meanP;
    items.forEach((it, i) => xById.set(it.n.id, placed[i] + shift));
  };
  for (const r of [...rowIndices].reverse()) placeRow(rows.get(r)!);

  const nodes: FlowNodeType[] = derived.nodes.map(n => {
    const v = visuals[n.role] ?? {};
    const saved = getPath(n.mbo ?? {}, posField);
    const position = isPosition(saved)
      ? { x: saved.x, y: saved.y }
      : { x: xById.get(n.id) ?? 0, y: (rowOf.get(n.role) ?? 0) * ROW_GAP_Y };
    return {
      id: n.id,
      type: 'flowNode',
      position,
      // Read-only nodes: not deletable (only edges can be removed).
      deletable: false,
      data: {
        label: v.label ?? n.role,
        category: v.label ?? n.role,
        color: v.color ?? FALLBACK_VISUAL.color,
        icon: v.icon ?? FALLBACK_VISUAL.icon,
        item: n.mbo ?? {},
        role: n.role,
        sides: n.sides.map(s => ({
          ...s,
          icon: visuals[s.role]?.icon,
          color: visuals[s.role]?.color,
          label: visuals[s.role]?.label,
        })),
        resources: n.resources,
        cardSchema: opts.cardSchema,
      },
    };
  });

  return { nodes, edges };
};

/** Main→main edges stored in `relatedNode`; null when none exist (⇒ use auto). */
const collectPersistedEdges = (
  nodes: IDerivedNode[],
  _mboById: Map<string, Obj>,
  config: IDeriveConfig
): IDerivedEdge[] | null => {
  const relField = config.relationField;
  if (!relField) return null;
  const tgtPath = config.relationTargetPath ?? 'node.id';
  const nodeIds = new Set(nodes.map(n => n.id));
  const edges: IDerivedEdge[] = [];
  for (const n of nodes) {
    const raw = getPath(n.mbo ?? {}, relField);
    const entries = Array.isArray(raw) ? raw : raw ? [raw] : [];
    for (const e of entries) {
      if ((e as Obj)?.toBeDeleted) continue;
      const tid = getPath(e, tgtPath) as string;
      if (tid && nodeIds.has(tid) && tid !== n.id)
        edges.push({ source: n.id, target: tid, rule: 'persisted' });
    }
  }
  return edges.length ? edges : null;
};
